// SharedSidebarNavigation.js
// Enterprise-grade reusable sidebar component
// Single source of truth for navigation across all authenticated scenes

import { auth } from '../firebaseConfig.js';

/**
 * Get standardized user display info
 * @returns {Object} {initials, displayName, role}
 */
export function getStandardizedUserInfo() {
    const user = auth.currentUser;
    if (!user) {
        return {
            initials: 'EX',
            displayName: 'Executive',
            role: 'Executive'
        };
    }
    
    // STANDARDIZED: Always prefer displayName, fallback to email
    const displayName = user.displayName || user.email || 'Executive';
    
    // Get initials from displayName or email
    const initials = displayName.length >= 2 
        ? displayName.substring(0, 2).toUpperCase() 
        : 'EX';
    
    return {
        initials,
        displayName,
        role: 'Executive'
    };
}

/**
 * Create consistent sidebar navigation for all scenes
 * @param {Object} config - Configuration options
 * @param {string} config.activeScene - Current scene key to highlight (e.g., 'dashboard', 'analytics', 'philosophy-flow')
 * @param {string} config.userInitials - User initials for avatar (optional - auto-detected if not provided)
 * @param {string} config.userName - User display name (optional - auto-detected if not provided)
 * @param {string} config.userRole - User role/title (optional - defaults to 'Executive')
 * @returns {string} Complete sidebar HTML
 */
export function createSidebarNavigation(config = {}) {
    // Auto-detect user info if not provided
    const standardUserInfo = getStandardizedUserInfo();
    
    const {
        activeScene = 'dashboard',
        userInitials = standardUserInfo.initials,
        userName = standardUserInfo.displayName,
        userRole = standardUserInfo.role
    } = config;
    
    // Helper to check if nav item should be active
    const isActive = (sceneKey) => activeScene === sceneKey ? 'active' : '';
    
    return `
        <aside class="sidebar">
            <div class="sidebar-header">
                <div class="logo">
                    <div class="logo-icon">D</div>
                    <div class="logo-text">
                        <div>DeckOps.ai</div>
                        <div class="logo-subtitle">Executive Platform</div>
                    </div>
                </div>
            </div>
            
            <nav class="nav-menu">
                <div class="nav-section">
                    <div class="nav-section-title">Main</div>
                    <a href="#" class="nav-item ${isActive('dashboard')}" data-action="dashboard">
                        <span class="nav-icon">🏠</span>
                        <span class="nav-label">Dashboard</span>
                    </a>
                    <a href="#" class="nav-item ${isActive('analytics')}" data-action="analytics">
                        <span class="nav-icon">📊</span>
                        <span class="nav-label">Analytics</span>
                    </a>
                    <a href="#" class="nav-item ${isActive('ppt-hero-analytics')}" data-action="ppt-hero-analytics">
                        <span class="nav-icon">📈</span>
                        <span class="nav-label">PPT Hero Analytics</span>
                        <span class="nav-badge">Detailed</span>
                    </a>
                    <a href="#" class="nav-item ${isActive('hall-of-fame')}" data-action="hall-of-fame">
                        <span class="nav-icon">🏆</span>
                        <span class="nav-label">Hall of Fame</span>
                        <span class="nav-badge">New</span>
                    </a>
                </div>
            
                <div class="nav-section">
                    <div class="nav-section-title">Training Modules</div>
                    <a href="#" class="nav-item ${isActive('game-center')}" data-action="game-center">
                        <span class="nav-icon">🎮</span>
                        <span class="nav-label">Game Center</span>
                    </a>
                    <a href="#" class="nav-item ${isActive('ppt-hero')}" data-game="ppt-hero">
                        <span class="nav-icon">📈</span>
                        <span class="nav-label">PPT Hero</span>
                    </a>
                    <a href="#" class="nav-item ${isActive('philosophy-flow')}" data-game="philosophy-flow">
                        <span class="nav-icon">🎨</span>
                        <span class="nav-label">Philosophy Flow</span>
                    </a>
                    <a href="#" class="nav-item disabled" title="Coming Soon">
                        <span class="nav-icon">⚔️</span>
                        <span class="nav-label">Slide Wars</span>
                        <span class="nav-badge">Soon</span>
                    </a>
                    <a href="#" class="nav-item disabled" title="Coming Soon">
                        <span class="nav-icon">🏛️</span>
                        <span class="nav-label">Classify Tech</span>
                        <span class="nav-badge">Soon</span>
                    </a>
                </div>
                
                <div class="nav-section">
                    <div class="nav-section-title">Settings</div>
                    <a href="#" class="nav-item ${isActive('my-library')}" data-action="my-library">
                        <span class="nav-icon">📚</span>
                        <span class="nav-label">My Library</span>
                    </a>
                    <a href="#" class="nav-item ${isActive('profile')}" data-action="profile">
                        <span class="nav-icon">⚙️</span>
                        <span class="nav-label">Preferences</span>
                    </a>
                    <a href="#" class="nav-item ${isActive('help')}" data-action="help">
                        <span class="nav-icon">❓</span>
                        <span class="nav-label">Help & Support</span>
                    </a>
                </div>
            </nav>
            
            <div class="sidebar-footer">
                <div class="user-profile">
                    <div class="user-avatar">${userInitials}</div>
                    <div class="user-info">
                        <div class="user-name">${userName}</div>
                        <div class="user-role">${userRole}</div>
                    </div>
                    <span style="color: var(--text-muted);">›</span>
                </div>
            </div>
        </aside>
    `;
}

/**
 * Get scene key from scene name for active state
 * @param {string} sceneName - Full scene name
 * @returns {string} Simplified scene key
 */
export function getSceneKeyForSidebar(sceneName) {
    const sceneKeyMap = {
        'AuthenticatedMenuSceneDOM': 'dashboard',
        'AnalyticsSceneDOM': 'analytics',
        'HallOfFameDesktopTemplate': 'hall-of-fame',
        'GameCenterSceneDOM': 'game-center',
        'PhilosophyFlowDesktopScene': 'philosophy-flow',
        'MyLibrarySceneDOM': 'my-library',
        'ProfileSceneDOMTemplate': 'profile',
        'AdminPanelSceneDOM': 'admin',
        'NotificationsSceneDOM': 'notifications'
    };
    
    return sceneKeyMap[sceneName] || 'dashboard';
}

export default {
    createSidebarNavigation,
    getSceneKeyForSidebar
};

